import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup, MatTab } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  DialogService,
  DIALOG_RETURN_TYPES,
  DialogComponent,
} from '@next/core-lib/dialog';
import type { AbstractDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { Product } from 'src/app/shared/models/shared.model';
import { COMPARISON_QUICKSIMULATION } from 'src/assets/data/feedProfitConstants';

import { BaselineComponent } from './baseline/baseline.component';
import { CompanyDetailsComponent } from './company-details/company-details.component';
import { SETTINGS_DIALOG_HOST } from './settings-dialog.token';
import {
  BaseLine,
  BaselineDelivery,
  Calculation,
  CompanyDetails,
  ExtendedBaseLine,
  FeederTableRow,
} from '../../models/feed-profit.model';
import * as actions from '../../store/feed-profit.actions';
import { getIsChanged, getSelectedCalculation } from '../../store/feed-profit.selectors';
import { BaselineSettingsComponent } from '../shared/baseline-settings/baseline-settings.component';
import { ExtendedBaselineSettingsComponent } from '../shared/extended-baseline-settings/extended-baseline-settings.component';

export interface SettingsDialogInputData extends AbstractDialogData {
  calculation: Calculation;
}
export interface SettingsDialogResultData {
  calculation: Calculation;
}

@Component({
  selector: 'app-settings-dialog',
  templateUrl: './settings-dialog.component.html',
  styleUrls: ['./settings-dialog.component.scss'],
  imports: [
    DialogComponent,
    MatTabGroup,
    MatTab,
    CompanyDetailsComponent,
    BaselineComponent,
    BaselineSettingsComponent,
    ExtendedBaselineSettingsComponent,
    TranslatePipe,
  ],
  providers: [
    { provide: SETTINGS_DIALOG_HOST, useExisting: forwardRef(() => SettingsDialogComponent) },
  ],
})
export class SettingsDialogComponent
  extends AbstractDialog<SettingsDialogResultData>
  implements OnInit, AfterViewInit
{
  calculation: Calculation;
  @ViewChild(forwardRef(() => CompanyDetailsComponent))
  companyDetailsComponent: CompanyDetailsComponent;
  @ViewChild(BaselineComponent) baselineComponent: BaselineComponent;
  @ViewChild(BaselineSettingsComponent) baselineSettingsComponent: BaselineSettingsComponent;
  @ViewChild(ExtendedBaselineSettingsComponent)
  extendedBaselineSettingsComponent: ExtendedBaselineSettingsComponent;
  selectedTabIndex = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SettingsDialogInputData,
    protected mdDialogRef: MatDialogRef<
      SettingsDialogComponent,
      AbstractDialogResultData<SettingsDialogResultData>
    >,
    private readonly dialog: DialogService,
    private readonly locale: LocaleService,
    private readonly store: Store<RootState>,
    private changeDetectionRef: ChangeDetectorRef,
  ) {
    super();
  }
  ngAfterViewInit() {
    this.changeDetectionRef.detectChanges();
  }
  ngOnInit(): void {
    this.store
      .select(getSelectedCalculation)
      .pipe(take(1))
      .subscribe((calculation) => {
        this.calculation = calculation;
      });
  }

  private getCalculationValuesAndConfirm() {
    const { name, cause, priceSetting, numberOfSilos, amountOfGrowFinisher } =
      this.companyDetailsComponent.form.value;
    const {
      calculatedPriceLargerThan25Kg,
      calculatedPriceLessThan25Kg,
      averageWeightPerDeadGrowFinisher,
      growthStartProcedure,
      growthEndProcedure,
    } = this.baselineSettingsComponent.form.value;
    const {
      squareMeterPerGrowFinisher,
      startWeight,
      growFinisherPrice,
      revenuePerKg,
      slaughteredWeightInKg,
      meatPercentage,
      loinDepthThicknessInMillimeters,
      backFatThicknessInMillimeters,
      growth,
      averageEnergyConversion,
      mortalityPercentage,
      cleaning,
      fertilizerPricePerCubicMeter,
      firstDeliveryPercentage,
      secondDeliveryDays,
      secondDeliveryPercentage,
      thirdDeliveryDays,
    } = this.extendedBaselineSettingsComponent.form.value;

    //check if fertilizerPerCubicMeter is calculated yet, if not. Calculate it.
    let fertilizerPerGrowFinisherInCubicMeters =
      this.extendedBaselineSettingsComponent.form.value.fertilizerPerGrowFinisherInCubicMeters;

    if (this.extendedBaselineSettingsComponent.form.valid && this.baselineComponent.form.valid) {
      const companyDetails = <CompanyDetails>{
        cause,
        priceSetting,
        numberOfSilos,
        amountOfGrowFinisher,
      };
      const baseLine = <BaseLine>{
        calculatedPriceLargerThan25Kg,
        calculatedPriceLessThan25Kg,
        averageWeightPerDeadGrowFinisher,
        growthStartProcedure,
        growthEndProcedure,
      };
      const extendedBaseLine = <ExtendedBaseLine>{
        squareMeterPerGrowFinisher,
        startWeight,
        growFinisherPrice,
        revenuePerKg,
        slaughteredWeightInKg,
        meatPercentage,
        loinDepthThicknessInMillimeters,
        backFatThicknessInMillimeters,
        growth,
        averageEnergyConversion,
        mortalityPercentage,
        cleaning,
        fertilizerPerGrowFinisherInCubicMeters,
        fertilizerPricePerCubicMeter,
        deliveries: <BaselineDelivery[]>[
          { index: 0, days: 0, percentage: firstDeliveryPercentage },
          {
            index: 1,
            days: secondDeliveryDays,
            percentage: secondDeliveryPercentage,
          },
          {
            index: 2,
            days: thirdDeliveryDays,
            percentage: 100 - firstDeliveryPercentage - secondDeliveryPercentage,
          },
        ],
      };
      if (
        fertilizerPerGrowFinisherInCubicMeters == null ||
        isNaN(fertilizerPerGrowFinisherInCubicMeters) ||
        isFinite(fertilizerPerGrowFinisherInCubicMeters)
      ) {
        const calculation = <Calculation>{
          ...this.calculation,
          companyDetails,
          extendedBaseLine,
          baseLine,
          comparisonSimulation: COMPARISON_QUICKSIMULATION,
        };
        fertilizerPerGrowFinisherInCubicMeters =
          this.extendedBaselineSettingsComponent.calculateM3Fertilizer(calculation);
      }

      const newCalculation: Calculation = {
        ...this.calculation,
        name,
        genders: this.baselineComponent.form.value.genders,
        country: this.baselineComponent.form.value.country,
        companyDetails,
        baseLine,
        extendedBaseLine: {
          ...extendedBaseLine,
          fertilizerPerGrowFinisherInCubicMeters,
        },
      };

      const calculation = {
        ...this.calculation,
        name,
        genders: this.baselineComponent.form.value.genders,
        country: this.baselineComponent.form.value.country,
        companyDetails,
        baseLine,
        extendedBaseLine: {
          ...extendedBaseLine,
          fertilizerPerGrowFinisherInCubicMeters,
        },
        comparisonSimulation: this.baselineComponent.getQuickComparisonSimulation(newCalculation),
        extendedComparisonSimulation:
          this.baselineComponent.getComparisonSimulation(newCalculation),
      } as Calculation;

      this.confirm({
        calculation,
      });
    }
  }

  save() {
    if (this.companyDetailsComponent.form.invalid) {
      this.validateAllFormFields(this.companyDetailsComponent.form);
      this.selectedTabIndex = 0;
      return;
    } else if (this.extendedBaselineSettingsComponent.form.invalid) {
      this.validateAllFormFields(this.extendedBaselineSettingsComponent.form);
      this.selectedTabIndex = 3;
      return;
    }

    const allProducts: Product[] = [];
    allProducts.push(
      (
        this.baselineComponent.baselineFeedersComponent?.calculatedTableComponent?.tableConfig
          ?.dataSource?.data[0] as FeederTableRow
      ).product,
    );

    this.baselineComponent.baselineFeedersComponent?.tableComponents.forEach((comp) =>
      comp.tableConfig.dataSource.data.forEach((data: FeederTableRow) =>
        allProducts.push(data?.product),
      ),
    );

    if (!allProducts.every((row) => row && row.code && row.code !== '')) {
      this.selectedTabIndex = 1;
      this.baselineComponent.baselineFeedersComponent?.tableComponents.forEach((comp) => {
        comp.productError = true;
        if (comp.calculatedTableComponent) {
          comp.calculatedTableComponent.productError = true;
        }
      });
      return;
    }

    this.store.dispatch(actions.setIsChanged({ isChanged: false }));
    this.getCalculationValuesAndConfirm();
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  calculationSettingsChanged($event: Calculation) {
    this.calculation = $event;
  }

  selectedTabChange($event: MatTabChangeEvent) {
    this.calculation = this.getCalculation();

    switch ($event.index) {
      case 0:
        this.companyDetailsComponent.setCompanyDetails(this.calculation);
        break;
      case 2:
        this.baselineSettingsComponent.setBaseLineSettingsValues(this.calculation);
        break;
      case 3:
        this.extendedBaselineSettingsComponent.setTableValues(this.calculation);
        break;
      default:
        break;
    }
  }

  cancel() {
    this.store
      .select(getIsChanged)
      .pipe(take(1))
      .subscribe((isChanged) => {
        if (
          this.companyDetailsComponent.form.dirty ||
          this.baselineSettingsComponent.form.dirty ||
          this.extendedBaselineSettingsComponent.form.dirty ||
          isChanged
        ) {
          this.dialog
            .pendingChanges({
              title: this.locale.translate(
                'feed-profit.detail.extended-simulation.item-cancellation-title',
              ),
              subTitle: this.locale.translate(
                'feed-profit.detail.extended-simulation.item-cancellation-subtitle',
              ),
              pushHistoryState: false,
            })
            .subscribe((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                this.mdDialogRef.close();
                this.store.dispatch(actions.setIsChanged({ isChanged: false }));
              } else {
                this.canceling = false;
                this.mdDialogRef.disableClose = true;
              }
            });
        } else {
          this.mdDialogRef.close();
        }
      });
  }

  getCalculation() {
    const companyDetails = this.companyDetailsComponent.getCalculation();

    return {
      ...this.calculation,
      name: companyDetails.name,
      companyDetails: companyDetails.companyDetails,
      baseLine: this.baselineSettingsComponent.getCalculation().baseLine,
      extendedBaseLine: this.extendedBaselineSettingsComponent.getCalculation().extendedBaseLine,
    };
  }
}
